
import { Component, Prop, Vue } from 'vue-property-decorator'
import { MessageBox } from 'element-ui'
import { useCultureSubsidyStore } from '@/stores/cultureSubsidy'
import { findOperationConflicts, isOperationActive } from '../utils/calculator'
import { SpecialOperation } from '../types'

@Component({ name: 'OperationsPanel' })
export default class extends Vue {
  @Prop({ default: false }) editable!: boolean
  @Prop({ default: '' }) caretakerId!: string

  store = useCultureSubsidyStore()
  dialogVisible = false
  boostMode: 'rate' | 'value' = 'rate'
  form = {
    name: '',
    type: 'global' as 'global' | 'per_product',
    productId: '',
    range: [] as string[],
    ratePercent: 40,
    value: 10
  }

  get currentYear(): number {
    return new Date().getFullYear()
  }

  get months(): string[] {
    return this.$moment.monthsShort()
  }

  get eligibleProducts() {
    return this.store.products.filter(product => product.eligible)
  }

  get warningConflicts() {
    return findOperationConflicts(this.store.operations).filter(conflict => conflict.kind === 'warning')
  }

  isActive(operation: SpecialOperation): boolean {
    return isOperationActive(operation)
  }

  barStyle(operation: SpecialOperation): Record<string, string> {
    const start = new Date(operation.startDate)
    const end = new Date(operation.endDate)
    const yearStart = new Date(start.getFullYear(), 0, 1).getTime()
    const yearEnd = new Date(start.getFullYear(), 11, 31).getTime()
    const span = yearEnd - yearStart
    const left = Math.max(0, ((start.getTime() - yearStart) / span) * 100)
    const right = Math.min(100, ((end.getTime() - yearStart) / span) * 100)
    return {
      left: `${left}%`,
      width: `${Math.max(2, right - left)}%`
    }
  }

  formatWindow(start: string, end: string): string {
    return `${this.$moment(start).format('D MMM')} → ${this.$moment(end).format('D MMM YYYY')}`
  }

  openCreate(): void {
    this.form = {
      name: '',
      type: 'global',
      productId: this.eligibleProducts[0]?.id || '',
      range: [],
      ratePercent: 40,
      value: 10
    }
    this.boostMode = 'rate'
    this.dialogVisible = true
  }

  async create(): Promise<void> {
    if (!this.form.name || this.form.range.length !== 2) {
      this.$message.warning(this.$t('cultureSubsidy.operations.incomplete') as string)
      return
    }
    try {
      await this.store.createOperation({
        name: this.form.name,
        type: this.form.type,
        productId: this.form.type === 'per_product' ? this.form.productId : null,
        startDate: this.form.range[0],
        endDate: this.form.range[1],
        boostedRate: this.boostMode === 'rate' ? this.form.ratePercent / 100 : null,
        boostedValue: this.boostMode === 'value' ? this.form.value : null
      }, this.caretakerId || undefined)
      this.dialogVisible = false
      this.$message.success(this.$t('cultureSubsidy.operations.created') as string)
    } catch (error) {
      this.$message.error((error as Error).message)
    }
  }

  async remove(id: string): Promise<void> {
    try {
      await MessageBox.confirm(
        this.$t('cultureSubsidy.operations.deleteConfirm') as string,
        this.$t('cultureSubsidy.operations.delete') as string,
        { type: 'warning' }
      )
    } catch {
      return
    }
    await this.store.deleteOperation(id, this.caretakerId || undefined)
  }
}
